import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import Isvg from 'react-inlinesvg';
import { useSelector } from 'react-redux';

import Badge from 'components/Badge';
import { Card } from 'components/Card';
import { Dropdown, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import Message from 'components/Message';
import SvgQuestion from 'components/Svgs/Question';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';
import When from 'components/When';

import { PFE_ICONS, ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';

import { bem } from 'helpers/class-name';
import { isVenueDashboardSubtypeFoodAndBeverageById } from 'helpers/ia-logic';

import { selectActiveVenueId } from 'selectors/general';

import { CAMPAIGN_LEVEL } from './Campaigns.helpers';
import messages from './Campaigns.messages';
import './Campaigns.scss';

const { element } = bem('campaigns');

const CampaignFlexCell = ({ children, label, description, className, values, labelClassName = 'mb-s' }) => (
  <div className={element('flex-cell', 'direction-column', className)}>
    <Message
      as="div"
      className={classNames('text--sm text--semibold text--ui70', labelClassName)}
      message={label}
      values={values}
    />
    {children}
    <When predicate={description}>
      <Message as="div" className="mt-t text--ui70" message={description} />
    </When>
  </div>
);

CampaignFlexCell.propTypes = {
  children: PropTypes.node,
  label: intlMessage,
  description: intlMessage,
  className: PropTypes.string,
  values: PropTypes.shape({}),
  labelClassName: PropTypes.string,
};

const renderCampaignStatus = (hasStarted, hasEnded) => {
  let message = null;

  if (hasStarted) {
    message = messages.table.statusActive;
  }

  if (hasEnded) {
    message = messages.table.statusEnded;
  }

  if (!hasStarted && !hasEnded) {
    message = messages.table.statusUpcoming;
  }

  return (
    <Badge info={hasStarted && !hasEnded} notice={!hasStarted && !hasEnded} disabled={hasEnded}>
      <Message message={message} />
    </Badge>
  );
};

const CampaignsRowDropdown = ({ isEditable, handleDropdownClick, editUrl, hasEnded }) => (
  <Dropdown attachment="right">
    {({ toggleMenu }) => (
      <>
        <button type="button" onClick={toggleMenu}>
          <Isvg src={PFE_ICONS.threeDots} />
        </button>
        <DropdownMenu>
          {isEditable ? (
            <DropdownItem onClick={handleDropdownClick(editUrl)}>
              <Message message={messages.table.menuEdit} />
            </DropdownItem>
          ) : (
            <DropdownItem onClick={() => {}}>
              <Message
                message={hasEnded ? messages.table.menuEnded : messages.table.menuUneditable}
                className="text--light"
              />
            </DropdownItem>
          )}
        </DropdownMenu>
      </>
    )}
  </Dropdown>
);

CampaignsRowDropdown.propTypes = {
  isEditable: PropTypes.bool,
  handleDropdownClick: PropTypes.func,
  editUrl: PropTypes.string,
  hasEnded: PropTypes.bool,
};

const campaignRowProps = {
  isAppointmentTypeVenue: PropTypes.bool,
  amount: PropTypes.number,
  campaignName: PropTypes.string,
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    })
  ),
  currency: PropTypes.string,
  discountDecimal: PropTypes.number,
  editUrl: PropTypes.string,
  endDateTime: PropTypes.instanceOf(Date),
  handleDropdownClick: PropTypes.func,
  hasEnded: PropTypes.bool,
  hasStarted: PropTypes.bool,
  isClassType: PropTypes.bool,
  isEditable: PropTypes.bool,
  lastUpdatedBy: PropTypes.string,
  lastUpdatedOn: PropTypes.string,
  name: PropTypes.string,
  partnerCampaignType: PropTypes.shape({
    name: PropTypes.string,
  }),
  redemptions: PropTypes.number,
  startDateTime: PropTypes.instanceOf(Date),
  partnerCampaignId: PropTypes.number,
};

function CampaignsFlexTableRow({
  isAppointmentTypeVenue,
  amount,
  campaignName,
  classes,
  currency,
  discountDecimal,
  editUrl,
  endDateTime,
  handleDropdownClick,
  hasEnded,
  hasStarted,
  isClassType,
  isEditable,
  lastUpdatedBy,
  lastUpdatedOn,
  name,
  partnerCampaignType,
  redemptions,
  startDateTime,
  partnerCampaignId,
}) {
  const venueId = useSelector(selectActiveVenueId);

  const isFoodAndBeverage = isVenueDashboardSubtypeFoodAndBeverageById(venueId);
  const allServicesMessage = (() => {
    if (isFoodAndBeverage) {
      return messages.table.allItems;
    }

    if (isAppointmentTypeVenue) {
      return messages.table.allServices;
    }

    return messages.table.allClasses;
  })();

  return (
    <div className="flex wrap-wrap p-r list-item">
      <div className="flex basis-100">
        <CampaignFlexCell className="mb-r grow-1" label={messages.table.headerCampaigns} labelClassName="mb-sp">
          <div className={element('highlight-bold')}>{name}</div>
          <div>
            <Message message={messages.nameOverrides[partnerCampaignType.name] || campaignName} />

            <TooltipToggle
              name={`tooltip-campaign-description-${partnerCampaignId}`}
              className={element('tooltip-toggle', 'ml-tiny')}>
              <SvgQuestion className={element('tooltip-question-mark')} />
            </TooltipToggle>
            <TooltipContent
              className={element('tooltip-content')}
              name={`tooltip-campaign-description-${partnerCampaignId}`}
              place="right">
              <Message message={messages.descriptionOverrides[partnerCampaignType.name]} />
            </TooltipContent>
          </div>
          <Message
            as="div"
            className={element('last-update', 'block')}
            message={messages.table.updatedMessage}
            values={{ dateTime: Date.parse(lastUpdatedOn), name: lastUpdatedBy }}
          />
        </CampaignFlexCell>
        <CampaignsRowDropdown
          isEditable={isEditable}
          handleDropdownClick={handleDropdownClick}
          editUrl={editUrl}
          hasEnded={hasEnded}
        />
      </div>

      <CampaignFlexCell
        className="mb-r basis-100"
        label={messages.table.headerDuration}
        title={messages.table.duration}>
        <Message message={messages.table.duration} values={{ startDateTime, endDateTime }} />
      </CampaignFlexCell>

      <CampaignFlexCell className="mb-r basis-100" label={messages.table.headerServices}>
        {isClassType ? (
          <ServiceList
            services={classes}
            partnerCampaignId={partnerCampaignId}
            mobile
            isAppointmentTypeVenue={isAppointmentTypeVenue}
          />
        ) : (
          <Message message={allServicesMessage} />
        )}
      </CampaignFlexCell>

      <CampaignFlexCell label={messages.table.headerAmount} className="grow-1">
        <Message className={element('highlight-bold')} value={discountDecimal} format="percent" />
      </CampaignFlexCell>

      <CampaignFlexCell label={messages.table.headerRedemptions} className="grow-1">
        <Message value={redemptions} />
      </CampaignFlexCell>

      <CampaignFlexCell label={messages.table.headerEarnings} className="grow-1">
        <Message value={amount} format="currency" currency={currency} />
      </CampaignFlexCell>

      <CampaignFlexCell label={messages.table.headerStatus} className="grow-1">
        {hasStarted && (
          <Badge info={hasStarted && !hasEnded} disabled={hasEnded}>
            <Message message={hasEnded ? messages.table.statusEnded : messages.table.statusActive} />
          </Badge>
        )}
      </CampaignFlexCell>
    </div>
  );
}

CampaignsFlexTableRow.propTypes = campaignRowProps;

const ServiceList = ({ services, mobile = false, partnerCampaignId, isAppointmentTypeVenue }) => {
  const venueId = useSelector(selectActiveVenueId);

  if (mobile) {
    return services.map(service => service.name).join(', ');
  }

  const firstTwo = services
    .slice(0, 2)
    .map(service => service.name)
    .join(', ');

  if (services.length <= 2) {
    return firstTwo;
  }

  const isFoodAndBeverage = isVenueDashboardSubtypeFoodAndBeverageById(venueId);
  const highlightMessage = (() => {
    if (isFoodAndBeverage) {
      return messages.table.itemsSelected;
    }
    if (isAppointmentTypeVenue) {
      return messages.table.servicesSelected;
    }
    return messages.table.classesSelected;
  })();

  return (
    <>
      {firstTwo}
      <TooltipToggle name={`tooltip-more-services-${partnerCampaignId}`}>
        <Message
          as="div"
          className={element('more-services')}
          message={messages.table.moreServices}
          values={{ count: services.length - 2 }}
        />
      </TooltipToggle>
      <TooltipContent
        name={`tooltip-more-services-${partnerCampaignId}`}
        className={element('tooltip-content')}
        place="bottom"
        hoverable>
        <Message
          as="div"
          className={element('tooltip-highlight', 'mb-small')}
          message={highlightMessage}
          values={{ count: services.length }}
        />
        <div className={element('tooltip-service-list')}>{services.map(service => service.name).join(', ')}</div>
      </TooltipContent>
    </>
  );
};

ServiceList.propTypes = {
  services: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    })
  ),
  mobile: PropTypes.bool,
  partnerCampaignId: PropTypes.number,
  isAppointmentTypeVenue: PropTypes.bool,
};

const CampaignsTableRow = props => {
  const {
    isAppointmentTypeVenue,
    amount,
    campaignName,
    classes,
    currency,
    discountDecimal,
    editUrl,
    endDateTime,
    handleDropdownClick,
    hasEnded,
    hasStarted,
    isClassType,
    isEditable,
    lastUpdatedBy,
    lastUpdatedOn,
    name,
    partnerCampaignType,
    redemptions,
    startDateTime,
    partnerCampaignId,
  } = props;

  const venueId = useSelector(selectActiveVenueId);

  const isFoodAndBeverage = isVenueDashboardSubtypeFoodAndBeverageById(venueId);
  const allClassesServicesMessage = (() => {
    if (isFoodAndBeverage) {
      return messages.table.allItems;
    }

    if (isAppointmentTypeVenue) {
      return messages.table.allServices;
    }

    return messages.table.allClasses;
  })();

  const classesFormatted = isClassType ? (
    <ServiceList
      services={classes}
      partnerCampaignId={partnerCampaignId}
      isAppointmentTypeVenue={isAppointmentTypeVenue}
    />
  ) : (
    <Message message={allClassesServicesMessage} />
  );

  return (
    <tr className={element('tr')}>
      <td className={element('td')}>
        <span className={element('highlight-bold', 'block')}>{name}</span>
        <span className="block">
          <Message message={messages.nameOverrides[partnerCampaignType.name] || campaignName} />

          <TooltipToggle
            name={`tooltip-campaign-description-${partnerCampaignId}`}
            className={element('tooltip-toggle', 'ml-tiny')}>
            <SvgQuestion className={element('tooltip-question-mark')} />
          </TooltipToggle>
          <TooltipContent
            className={element('tooltip-content')}
            name={`tooltip-campaign-description-${partnerCampaignId}`}
            place="right">
            <Message message={messages.descriptionOverrides[partnerCampaignType.name]} />
          </TooltipContent>
        </span>
        <Message
          className={element('last-update', 'block')}
          message={messages.table.updatedMessage}
          values={{ dateTime: Date.parse(lastUpdatedOn), name: lastUpdatedBy }}
        />
      </td>

      <Message
        value={discountDecimal}
        format="percent"
        as="td"
        className={element('td', { centered: true, bold: true })}
      />

      <Message
        message={messages.table.duration}
        values={{ startDateTime, endDateTime }}
        as="td"
        className={element('td')}
      />

      <td className={element('td')}>{classesFormatted}</td>

      <td className={element('td', { centered: true })}>{renderCampaignStatus(hasStarted, hasEnded)}</td>
      <Message value={redemptions} as="td" className={element('td', { alignRight: true })} />
      <Message
        value={amount}
        format="currency"
        currency={currency}
        as="td"
        className={element('td', { alignRight: true })}
      />
      <td className={element('td', 'pr-t')}>
        <CampaignsRowDropdown
          isEditable={isEditable}
          handleDropdownClick={handleDropdownClick}
          editUrl={editUrl}
          hasEnded={hasEnded}
        />
      </td>
    </tr>
  );
};

CampaignsTableRow.propTypes = campaignRowProps;

function CampaignsTable({ campaigns, isAppointmentTypeVenue }) {
  const navigate = useNavigate();
  const handleDropdownClick = editUrl => () => navigate(editUrl);

  const [flexTableBody, tableBody] = [CampaignsFlexTableRow, CampaignsTableRow].map(Component =>
    campaigns.map(campaign => {
      const {
        discountPercentage,
        endDateTime,
        partnerCampaignId,
        partnerCampaignType,
        startDateTime,
        revenue: { amount, currency },
      } = campaign;
      const isClassType = partnerCampaignType.level === CAMPAIGN_LEVEL.CLASS;

      const key =
        partnerCampaignId ||
        [
          partnerCampaignType.level,
          partnerCampaignType.name,
          discountPercentage,
          startDateTime.toISOString(),
          !endDateTime ? 'now' : endDateTime.toISOString(),
        ].join('-');

      const props = {
        ...campaign,
        partnerCampaignId,
        amount,
        currency,
        handleDropdownClick,
        isClassType,
        isAppointmentTypeVenue,
      };
      return <Component key={key} {...props} />;
    })
  );

  const venueId = useSelector(selectActiveVenueId);
  const isFoodAndBeverage = isVenueDashboardSubtypeFoodAndBeverageById(venueId);
  const classesServicesHeader = (() => {
    if (isFoodAndBeverage) {
      return messages.table.headerItems;
    }

    if (isAppointmentTypeVenue) {
      return messages.table.headerServices;
    }

    return messages.table.headerClasses;
  })();

  return (
    <Card flush className={element('table-container', 'mb-r')}>
      <div className={element('flex-table')}>{flexTableBody}</div>
      <table className={element('table')}>
        <thead>
          <tr>
            <Message message={messages.table.headerCampaigns} as="th" className={element('th')} />
            <Message message={messages.table.headerAmount} as="th" className={element('th', { centered: true })} />
            <Message message={messages.table.headerDuration} as="th" className={element('th', { small: true })} />
            <Message message={classesServicesHeader} as="th" className={element('th')} />
            <Message message={messages.table.headerStatus} as="th" className={element('th', { centered: true })} />
            <th className={element('th', { centered: true })}>
              <Message message={messages.table.headerRedemptions} />
              <TooltipToggle name="redemptions-tooltip" className={element('tooltip-toggle')}>
                <Isvg src={ICONS.infoGrey} className={element('info-icon')} />
              </TooltipToggle>
              <TooltipContent name="redemptions-tooltip" className={element('tooltip-content')} hoverable place="right">
                <Message
                  message={messages.table.tooltipRedemptions}
                  values={{ strong: text => <strong>{text}</strong> }}
                />
              </TooltipContent>
            </th>
            <th className={element('th', { centered: true })}>
              <Message message={messages.table.headerEarnings} />
              <TooltipToggle name="revenue-tooltip" className={element('tooltip-toggle')}>
                <Isvg src={ICONS.infoGrey} className={element('info-icon')} />
              </TooltipToggle>
              <TooltipContent name="revenue-tooltip" className={element('tooltip-content')} hoverable place="right">
                <Message message={messages.table.tooltipEarnings} />
              </TooltipContent>
            </th>
            <th />
          </tr>
        </thead>
        <tbody>{tableBody}</tbody>
      </table>
    </Card>
  );
}

CampaignsTable.propTypes = {
  campaigns: PropTypes.arrayOf(PropTypes.shape({})),
  isAppointmentTypeVenue: PropTypes.bool,
};

export default CampaignsTable;
